/**
 * Site-operator Growth Console routes (GROWTH-PLAN Curve 1).
 *
 * Access control: SITE_ADMIN only. This is the operator surface (not a workspace admin
 * surface) — funnel, acquisition attribution, generated SOPs, users, and feature flags.
 * The server enforces the same gate via `adminProcedure` (SITE_ADMIN role); this client
 * guard is purely for UX so non-operators see a clean "access denied" instead of errors.
 */
import { Suspense } from 'react'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { Loader2, ShieldAlert, Home } from 'lucide-react'
import { useSession } from '@/lib/auth-client'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/app-sidebar'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

export const Route = createFileRoute('/(protected)/ops')({
  component: OpsLayout,
})

function OpsLayout() {
  const { data: session, isPending } = useSession()
  const isOperator = session?.user?.role === 'SITE_ADMIN'

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    )
  }

  if (!isOperator) {
    return (
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset className="h-screen overflow-hidden">
          <div className="mx-auto max-w-2xl p-8">
            <Alert variant="destructive">
              <ShieldAlert className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-3">
                  <p className="font-medium">Operator access required</p>
                  <p className="text-sm">
                    The Growth Console is restricted to Glyde site operators (SITE_ADMIN).
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => window.location.assign('/')}
                  >
                    <Home className="mr-2 h-4 w-4" />
                    Return to app
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          </div>
        </SidebarInset>
      </SidebarProvider>
    )
  }

  return (
    <Suspense
      fallback={
        <div className="flex h-screen w-screen items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      }
    >
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset className="h-screen overflow-hidden">
          <ScrollArea className="h-screen" viewClassName="[&>div]:!block">
            <Outlet />
          </ScrollArea>
        </SidebarInset>
      </SidebarProvider>
    </Suspense>
  )
}
